// Extrait du Dashboard Finance — grille tableau de suivi Visites AMO,
// avec KPIs par avance, filtre par visiteur, recherche, filtre payée.
// Les boutons d'import Opération/Finance ne sont PAS dans le widget
// (actions globales du hub, à porter ailleurs si besoin).
// Données : /api/metier/stats/visites-amo
import { useState } from "react";
import { Eye, Search, Users, Loader2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { formatClientFullName } from "../../utils/clientFormat";
import { formatDateFR } from "../../utils/dateFormat";
import { useFinanceFetch } from "./useFinanceFetch";

const AVANCE_COLORS = {
  "Solde payée":   "#10b981",
  "Solde":         "var(--oh-stat-blue-text)",
  "Avance":        "var(--oh-stat-amber-text)",
  "Avance payée":  "var(--oh-stat-cyan-text)",
};

export default function FinanceVisitesSuiviWidget() {
  const { data, loading, error } = useFinanceFetch("/api/metier/stats/visites-amo");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterVisiteur, setFilterVisiteur] = useState("all");
  const [filterPayee, setFilterPayee] = useState("all");

  if (loading) {
    return (
      <div
        className="rounded-xl p-5 h-full flex items-center justify-center"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
      >
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="rounded-xl p-5"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text-muted)" }}
      >
        Erreur Suivi Visites AMO : {error}
      </div>
    );
  }

  const stats = data?.stats || {};
  const dossiers = data?.dossiers || [];
  const parAvance = data?.par_avance || [];
  const parVisiteur = data?.par_visiteur || [];

  const filtered = dossiers.filter((d) => {
    const nom = (d.client_nom || d.client_nom_famille || "").toLowerCase();
    if (searchTerm && !nom.includes(searchTerm.toLowerCase())) return false;
    if (filterVisiteur !== "all" && (d.visiteur_amo || "Non assigné") !== filterVisiteur) return false;
    if (filterPayee === "payee" && d.visite_payee !== true) return false;
    if (filterPayee === "non_payee" && d.visite_payee !== false) return false;
    if (filterPayee === "inconnu" && d.visite_payee != null) return false;
    return true;
  }).slice(0, 100);

  return (
    <div
      className="rounded-xl p-5 h-full"
      style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
      data-testid="finance-visites-suivi-widget"
    >
      <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
        <Eye className="w-4 h-4" style={{ color: "var(--oh-stat-amber-text)" }} />
        Suivi Visites AMO
        {stats.avec_visite != null && (
          <Badge
            className="ml-2 text-xs"
            style={{
              background: "rgba(245,158,11,0.15)",
              color: "var(--oh-stat-amber-text)",
              border: "1px solid rgba(245,158,11,0.3)",
            }}
          >
            {stats.avec_visite} visites
          </Badge>
        )}
      </h3>

      {/* KPIs Avance */}
      {parAvance.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {parAvance.map((a) => (
            <div
              key={a.statut}
              className="rounded-lg p-3 text-center"
              style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}
            >
              <p className="text-2xl font-bold" style={{ color: AVANCE_COLORS[a.statut] || "#6366f1" }}>
                {a.count}
              </p>
              <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{a.statut}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filtres par visiteur (badges cliquables) */}
      {parVisiteur.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {parVisiteur.map((v) => (
            <button
              key={v.visiteur}
              type="button"
              onClick={() => setFilterVisiteur(filterVisiteur === v.visiteur ? "all" : v.visiteur)}
              className="rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-all cursor-pointer"
              style={{
                background: filterVisiteur === v.visiteur ? "rgba(139,92,246,0.2)" : "var(--oh-bg-surface)",
                border: `1px solid ${filterVisiteur === v.visiteur ? "rgba(139,92,246,0.5)" : "var(--oh-border)"}`,
                color: "var(--oh-text)",
              }}
              aria-pressed={filterVisiteur === v.visiteur}
            >
              <Users className="w-3 h-3" />
              <span className="font-medium">{v.visiteur}</span>
              <span style={{ color: "#10b981" }}>{v.payees}</span>
              <span style={{ color: "var(--oh-text-muted)" }}>/</span>
              <span style={{ color: "var(--oh-stat-red-text)" }}>{v.non_payees}</span>
            </button>
          ))}
        </div>
      )}

      {/* Recherche + filtre payée */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
          <input
            type="text"
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm outline-none"
            aria-label="Rechercher un client"
            style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
          />
        </div>
        <select
          value={filterPayee}
          onChange={(e) => setFilterPayee(e.target.value)}
          className="rounded-lg px-3 py-2 text-sm outline-none"
          aria-label="Filtrer par statut de paiement"
          style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
        >
          <option value="all">Toutes</option>
          <option value="payee">Payées</option>
          <option value="non_payee">Non payées</option>
          <option value="inconnu">Non renseigné</option>
        </select>
      </div>

      {/* Tableau */}
      <div className="overflow-x-auto rounded-lg oh-responsive-table" style={{ border: "1px solid var(--oh-border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--oh-bg-surface)" }}>
              {["Client", "Réf.", "Visiteur", "Date visite", "Payée", "Montant AMO", "Facture", "Règlement", "Avance"].map((h) => (
                <th
                  key={h}
                  className={`px-3 py-2.5 font-medium ${h === "Payée" ? "text-center" : h === "Montant AMO" ? "text-right" : "text-left"}`}
                  style={{ color: "var(--oh-text-muted)", borderBottom: "1px solid var(--oh-border)" }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((d, idx) => (
              <tr
                key={d.dossier_id || idx}
                className="transition-colors hover:brightness-110"
                style={{ borderBottom: "1px solid var(--oh-border)" }}
              >
                <td className="px-3 py-2.5 font-medium" style={{ color: "var(--oh-text)" }}>
                  {d.client_nom || formatClientFullName({
                    civilite: d.client_civilite,
                    prenom: d.client_prenom,
                    nom: d.client_nom_famille,
                  }) || "-"}
                </td>
                <td className="px-3 py-2.5" style={{ color: "var(--oh-text-secondary)" }}>
                  {d.reference_contrat_amo || d.référence || "-"}
                </td>
                <td className="px-3 py-2.5" style={{ color: "var(--oh-text-secondary)" }}>
                  {d.visiteur_amo || "-"}
                </td>
                <td className="px-3 py-2.5" style={{ color: "var(--oh-text-secondary)" }}>
                  {formatDateFR(d.date_visite)}
                </td>
                <td className="px-3 py-2.5 text-center">
                  {d.visite_payee === true ? (
                    <Badge className="text-xs" style={{ background: "rgba(16,185,129,0.15)", color: "#10b981", border: "1px solid rgba(16,185,129,0.3)" }}>
                      Payée
                    </Badge>
                  ) : d.visite_payee === false ? (
                    <Badge className="text-xs" style={{ background: "rgba(239,68,68,0.15)", color: "var(--oh-stat-red-text)", border: "1px solid rgba(239,68,68,0.3)" }}>
                      Non payée
                    </Badge>
                  ) : (
                    <Badge className="text-xs" style={{ background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)", border: "1px solid var(--oh-border)" }}>
                      -
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right font-medium" style={{ color: d.montant_amo ? "#8b5cf6" : "var(--oh-text-muted)" }}>
                  {typeof d.montant_amo === "number" ? `${d.montant_amo.toLocaleString("fr-FR")} €` : d.montant_amo || "-"}
                </td>
                <td className="px-3 py-2.5 text-xs" style={{ color: d.reference_facture_amo ? "var(--oh-stat-blue-text)" : "var(--oh-text-muted)" }}>
                  {d.reference_facture_amo || "-"}
                </td>
                <td className="px-3 py-2.5 text-xs" style={{ color: "var(--oh-text-secondary)" }}>
                  {d.date_reglement ? formatDateFR(d.date_reglement) : (d.règlement || "-")}
                </td>
                <td className="px-3 py-2.5">
                  {d.statut_avance ? (
                    <Badge
                      className="text-xs"
                      style={{
                        background: d.statut_avance.includes("payée") ? "rgba(16,185,129,0.15)" : "rgba(245,158,11,0.15)",
                        color: d.statut_avance.includes("payée") ? "#10b981" : "var(--oh-stat-amber-text)",
                        border: `1px solid ${d.statut_avance.includes("payée") ? "rgba(16,185,129,0.3)" : "rgba(245,158,11,0.3)"}`,
                      }}
                    >
                      {d.statut_avance}
                    </Badge>
                  ) : (
                    <span style={{ color: "var(--oh-text-muted)" }}>-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-8" style={{ color: "var(--oh-text-muted)" }}>
            {dossiers.length === 0
              ? "Aucune donnée de visite AMO. Lancez un import depuis le hub Finance."
              : "Aucun résultat pour ces filtres."}
          </div>
        )}
      </div>
    </div>
  );
}
